// src/services/ApiKeyService.js
// CRUD and usage tracking for API keys used by external integrations.
// ADM-201: backend dual-write (Wave 27 demo-29) via /api/apikeys/state.

import storage from './StorageAdapter';
import { applyDualWriteMixin, makeDualWriteMixin } from './_dualWriteHelpers';
import apiKeysApi from '../api/apiKeys';

const STORAGE_KEY = 'api_keys';
const STATE_KEY = 'keys'; // backend state-key innehåller hela array:en

// Available permission scopes for API keys
export const API_KEY_SCOPES = [
  { id: 'read:georisk',    group: 'read' },
  { id: 'read:customers',  group: 'read' },
  { id: 'read:bwra',       group: 'read' },
  { id: 'read:compliance', group: 'read' },
  { id: 'export:data',     group: 'export' },
  { id: 'admin:reports',   group: 'admin' },
];

function generateKeyId() {
  return 'key_' + Math.random().toString(36).slice(2, 11) + Date.now().toString(36);
}

function generateApiKey() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let part = '';
  for (let i = 0; i < 32; i++) {
    part += chars[Math.floor(Math.random() * chars.length)];
  }
  return 'prms_' + part.slice(0, 8) + '_' + part.slice(8, 16) + '_' + part.slice(16, 24) + '_' + part.slice(24, 32);
}

class ApiKeyService {
  _entityKey(_baseKey) {
    // STORAGE_KEY 'api_keys' är globalt (inte entity-prefixed) i nuvarande UI.
    // För dual-write använder vi backend-tenant-scope via X-Tenant-Id.
    return STORAGE_KEY;
  }

  getKeys() {
    return storage.getJSON(STORAGE_KEY, []);
  }

  async _saveKeys(keys) {
    await this._dualWriteJson(STATE_KEY, keys);
  }

  async createKey(name, scopes = [], expiryDate = null) {
    const keys = this.getKeys();
    const newKey = {
      id: generateKeyId(),
      name: name.trim(),
      key: generateApiKey(),
      scopes: scopes,
      expiryDate: expiryDate || null,
      createdAt: new Date().toISOString(),
      usageCount: 0,
      lastUsedAt: null,
      isActive: true,
    };
    keys.push(newKey);
    await this._saveKeys(keys);
    return newKey;
  }

  async revokeKey(id) {
    const keys = this.getKeys().map(k =>
      k.id === id ? { ...k, isActive: false } : k
    );
    await this._saveKeys(keys);
  }

  async activateKey(id) {
    const keys = this.getKeys().map(k =>
      k.id === id ? { ...k, isActive: true } : k
    );
    await this._saveKeys(keys);
  }

  async deleteKey(id) {
    const keys = this.getKeys().filter(k => k.id !== id);
    await this._saveKeys(keys);
  }

  async recordUsage(id) {
    const keys = this.getKeys().map(k =>
      k.id === id
        ? { ...k, usageCount: (k.usageCount || 0) + 1, lastUsedAt: new Date().toISOString() }
        : k
    );
    await this._saveKeys(keys);
  }

  getKeyById(id) {
    return this.getKeys().find(k => k.id === id) || null;
  }

  notifyListeners() {
    // No-op
  }

  async initFromBackend() {
    return this._initFromBackend([STATE_KEY], []);
  }
}

applyDualWriteMixin(ApiKeyService, makeDualWriteMixin({
  storage,
  api: apiKeysApi,
  serviceName: 'ApiKeyService',
}));

const apiKeyService = new ApiKeyService();
export default apiKeyService;
